// BuildNewCompositePage - EXISTING
import React, { useState, useEffect, useMemo, useCallback, useContext, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { resolveLineItemLinks } from '../utils/quantityLinks';
import {
  fetchMaterials,
  fetchBreakdownData,
  fetchRegions,
  API_BASE_URL,
  fetchMurCategories,
  fetchNewMaterialCategories,
} from '../services/api';
import './PageLayout.css';
import './BuildNewCompositeLayout.css';
import QuantityCalculatorModal from './QuantityCalculatorModal';
import { evaluate } from 'mathjs';
import { logAction } from '../services/api';
import { auth } from '../firebaseConfig';
import { useRegion } from '../context/RegionContext';
import StyledDropdown from '../components/StyledDropdown';
import CategoryPickerModal from '../components/CategoryPickerModal';
import { AuthContext } from '../context/AuthContext';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { typeMatchesBase, rowsFromLineItems, resolveCopyCategory, isUnrenamedCopy } from '../utils/copyRequest';
import api from '../services/api';
import VisibilityToggle from '../components/VisibilityToggle';
import BuildTypeToggle from './BuildTypeToggle';
import SmartMaterialSearch from '../components/SmartMaterialSearch';
import { useCompanyRole } from '../hooks/useCompanyRole';
import { getNezoAppUrl } from '../utils/nezoApp';
import AddAssemblyModal from './AddAssemblyModal';
import { breakdownToRows, removeAssemblyBlock, rescaleAssemblyBlock, includesNote } from '../utils/expandAssembly';

// "× 2" on an added assembly's heading: commit on blur / Enter, so typing "2.5" doesn't rescale at "2."
function AssemblyScale({ value, onCommit }) {
  const [text, setText] = useState(String(value));
  useEffect(() => { setText(String(value)); }, [value]);
  const commit = () => {
    const n = Number(text);
    if (Number.isFinite(n) && n > 0 && n !== Number(value)) onCommit(n);
    else setText(String(value));
  };
  return (
    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 13, color: '#134e4a', fontWeight: 600 }}>
      ×
      <input
        type="text"
        inputMode="decimal"
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
        aria-label="How much of this assembly per unit"
        title="How much of this assembly goes into 1 unit of this material — every line under it follows"
        style={{ width: 70, padding: '4px 6px', border: '1px solid #99d5cc', borderRadius: 6, textAlign: 'right', marginBottom: 0 }}
      />
    </label>
  );
}

const DEBUG = true;
const dlog = (...a) => { if (DEBUG) console.log('[BuildNewCompositePage]', ...a); };

const TYPE_BASE = { CO2: 'Co2', BIO: 'Co2-Biogenic', WEIGHT: 'Weight', RATE: 'Rate' };


const getName = (c) =>
  c?.MeasurementUnitName || c?.CostMeasurementName || c?.Name || c?.Description || '';


// Return to Nezo after submitting. When Nezo opened this tab, closing it lands the user back on the
// Nezo tab they came from (which refreshes its materials on focus); when it can't be closed (opened
// from the desktop app, or by hand), go to Nezo instead.
function backToNezo() {
  window.close();
  setTimeout(() => { window.location.href = getNezoAppUrl(); }, 300);
}

function BuildNewCompositePage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { selectedRegion } = useRegion();
  const [searchParams] = useSearchParams();
  const { user } = useContext(AuthContext);
  const role = (user?.role || 'user').toLowerCase();
  const isDenied = role !== 'user';
  const { canReview, canUpload } = useCompanyRole();
  const [userComments, setUserComments] = useState('');
  const [showThanks, setShowThanks] = useState(false);
  const [thanksId, setThanksId] = useState('');
  // What the last submit actually did — captured before the form resets (which sets visibility back
  // to 'public', so the popup can't read it from state).
  const [thanksInfo, setThanksInfo] = useState({ visibility: 'public', autoApproved: false, costOnly: false });
  const [NewMaterialCategories, setNewMaterialCategories] = useState([]);

  // Hidden categories (e.g. retired NABERS/Greenstar shadow roots) are meant to be
  // filtered out of the composite picker — same as the admin New Material page.
  const visibleCompositeCategories = useMemo(
    () => NewMaterialCategories.filter((c) => !c.IsHidden),
    [NewMaterialCategories]
  );

  const [visibility, setVisibility] = useState('public');

  const [showDraftSaved, setShowDraftSaved] = useState(false);
  const [showForReviewSaved, setShowForReviewSaved] = useState(false);

  // which tab is active: 'existing' | 'create'
  const [materialTab, setMaterialTab] = useState('existing');
  // category picker state
  const [catOpen, setCatOpen]           = useState(false);
  const catPickerRef                    = useRef(null);
  // Close cat picker on outside click
  useEffect(() => {
    const handler = (e) => {
      if (catPickerRef.current && !catPickerRef.current.contains(e.target)) {
        setCatOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);


  // NEW: Custom material name state
  const [customMaterialName, setCustomMaterialName] = useState('');
  const [customMaterialDescription, setCustomMaterialDescription] = useState('');
  const [customMaterialUnit, setCustomMaterialUnit] = useState('');
  const [customMaterialCategoryId, setCustomMaterialCategoryId] = useState('');
  // "Cost only": a NEW material with no carbon/biogenic/weight build-up — priced in Nezo from the
  // account's Rate Library instead. Only meaningful on the Create New Material tab.
  const [costOnlyChecked, setCostOnlyChecked] = useState(false);
  // "Copy material" from My Materials: the source's name, which the copy must not keep
  const [copySourceName, setCopySourceName] = useState('');
  const copySeededRef = useRef(false);
  
  const [showNextPopup, setShowNextPopup] = useState(false);


  // region pretty name
  const [regions, setRegions] = useState([]);
  useEffect(() => { (async () => { try { setRegions(await fetchRegions()); } catch(e){ console.error(e);} })(); }, []);
  const regionName = useMemo(
    () => regions.find(x => x.id === selectedRegion)?.name || selectedRegion,
    [regions, selectedRegion]
  );

  const regionSuffix = useMemo(() => {
  const region = regions.find(x => x.id === selectedRegion);
  // NZ is the default (no suffix), everything else appends -XX
  if (!region || region.country_code === 'NZ') return '';
  return `-${region.country_code}`;
}, [regions, selectedRegion]);

  // search/select base
  const [materials, setMaterials] = useState([]);
  const [filteredMaterials, setFilteredMaterials] = useState([]);
  const [, setSearchQuery] = useState(''); // kept for the template prefill; the picker searches itself
  const [selectedMaterial, setSelectedMaterial] = useState('');
  const [seeded, setSeeded] = useState(false);

  // lookups (from the view; includes .Category)
  const [carbonCategories, setCarbonCategories] = useState([]);
  const [biogenicCategories, setBiogenicCategories] = useState([]);
  const [weightCategories, setWeightCategories] = useState([]);
  const [costCategories, setCostCategories] = useState([]);

  // unique lists for Category / Description options
  const uniqueCategoryOptions = (arr) => {
    const seen = new Set(); const out = [];
    for (const r of arr || []) {
      const c = r.Category?.trim();
      if (!c) continue;
      if (!seen.has(c)) { seen.add(c); out.push({ value: c, label: c }); }
    }
    return out;
  };
  const uniqueDescOptions = (arr, category) => {
    const seen = new Set(); const out = [];
    for (const r of arr || []) {
      if (category && r.Category !== category) continue;
      const n = getName(r); if (!n) continue;
      if (!seen.has(n)) { seen.add(n); out.push({ value: n, label: n }); }
    }
    return out;
  };
  // union of env lists for Category/Description (so Env can filter across all three metrics)
  const ENV_ALL = useMemo(
  () => [...carbonCategories, ...biogenicCategories, ...weightCategories],
  [carbonCategories, biogenicCategories, weightCategories]
);


  const findCategoryForDesc = useCallback((desc) => {
    const hit = ENV_ALL.find(x => getName(x) === desc) || costCategories.find(x => getName(x) === desc);
    return hit?.Category || '';
  }, [ENV_ALL, costCategories]);

  const carbonCatRef    = useRef([]);
  const biogenicCatRef  = useRef([]);
  const weightCatRef    = useRef([]);

  useEffect(() => { carbonCatRef.current   = carbonCategories;   }, [carbonCategories]);
  useEffect(() => { biogenicCatRef.current = biogenicCategories; }, [biogenicCategories]);
  useEffect(() => { weightCatRef.current   = weightCategories;   }, [weightCategories]);

  // ENV rows: { id, category, description, quantity, unit, carbon{}, bio{}, weight{}, locked, isHeading, heading }
  const [rows, setRows] = useState([]);
  const [rowHistory, setRowHistory] = useState({});

  // COST rows: { id, category, description, quantity, unit, rate{}, locked }
  const [costRows, setCostRows] = useState([]);
  const [addAssemblyOpen, setAddAssemblyOpen] = useState(false);

  // desc -> codes hints
  const [descToCodes, setDescToCodes] = useState({});

  // UI state
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  // modal
  const [modalOpen, setModalOpen] = useState(false);
  const [activeRowIdx, setActiveRowIdx] = useState(null);

  // grids (add Category as first column)
  const GRID_ENV = useMemo(() => '36px minmax(400px,2.8fr) 110px 120px 140px 140px 140px 100px', []);
  const GRID_COST = useMemo(() => 'minmax(400px,2.8fr) 110px 120px 140px 100px', []);

  // --- load lookups ---
  const loadLookups = useCallback(async () => {
    if (!selectedRegion) return;
    if (isDenied) { setLoading(false); return; } // don't fetch if denied
    setLoading(true);
    try {
      await loadNewMaterialCategories();

      const token = await auth.currentUser?.getIdToken();

      const [materialsResp, carbon, bio, weight, rate] = await Promise.all([
        api.get('/newmaterial/accessible', {
          headers: {
            Authorization: `Bearer ${token}`,
            'x-region': selectedRegion,
            'x-user-role': role,
            'x-user-id': auth.currentUser?.uid || 'unknown',
            'x-user-email': auth.currentUser?.email || '',
          },
        }),
        fetchMurCategories(`vquest-Co2${regionSuffix}`),
        fetchMurCategories(`vquest-Co2-Biogenic${regionSuffix}`),
        fetchMurCategories(`vquest-Weight${regionSuffix}`),
        fetchMurCategories(`vquest-Rate${regionSuffix}`),
      ]);
      const mats = materialsResp.data || [];
      setMaterials(mats);
      setFilteredMaterials(mats);
      setCarbonCategories(carbon.data || []);
      setBiogenicCategories(bio.data || []);
      setWeightCategories(weight.data || []);
      setCostCategories(rate.data || []);

      const carbonData   = carbon.data || [];
      const biogenicData = bio.data    || [];
      const weightData   = weight.data || [];

      setCarbonCategories(carbonData);
      setBiogenicCategories(biogenicData);
      setWeightCategories(weightData);
      setCostCategories(rate.data || []);

      // ✅ Update refs immediately so loadBaseMaterial sees fresh data
      carbonCatRef.current   = carbonData;
      biogenicCatRef.current = biogenicData;
      weightCatRef.current   = weightData;

      dlog('Lookups loaded');
      dlog('materials:', mats.length);
      dlog('carbonCategories:', (carbon.data || []).length);
      dlog('biogenicCategories:', (bio.data || []).length);
      dlog('weightCategories:', (weight.data || []).length);
      dlog('costCategories:', (rate.data || []).length);
    } catch (e) {
      console.error('Load lookups failed', e);
      setError('Failed to load lists. Try refresh.');
    } finally {
      setLoading(false);
    }
  }, [selectedRegion, isDenied, regionSuffix]);

  useEffect(() => {
    // always reset state on region change
    setSelectedMaterial('');
    setCustomMaterialName(''); // NEW: Reset custom name
    setCustomMaterialDescription(''); // NEW: Reset custom description
    setCustomMaterialUnit('');
    setVisibility('public'); 
    setCustomMaterialCategoryId('');
    setRows([]);
    setCostRows([]);
    setRowHistory({});
    setDescToCodes({});
  }, [selectedRegion]);

  useEffect(() => {
  loadLookups();
}, [loadLookups]);

  // helpers
  const findCat = (collection, name, codeHint) => {
    const byName = collection.find(c => getName(c) === name);
    if (byName) {
      dlog('findCat HIT — full object:', JSON.stringify(byName)); // ← change this line
      return byName;
    }
    if (codeHint) {
      const byCode = collection.find(c => String(c.MeasurementUnitRateId) === String(codeHint));
      if (byCode) return byCode;
    }
    if (DEBUG) dlog('findCat: NO MATCH', { name, codeHint });
    return undefined;
  };

const fillForMetric = useCallback((collection, name, quantity, codeHint) => {
  const c = findCat(collection, name, codeHint);
  if (!c) return { measurementId: '', code:'', unit:'', value:0, total:0 };
  const value = Number(c.Value || 0);
  const q = Number(quantity || 0);
  return {
    measurementId: c.MeasurementId || '',
    code: c.MeasurementUnitRateId || '',
    unit: c.QuantityTypeName || '',
    value,
    total: value * q,
  };
}, []); // Empty deps - only uses its parameters and calls findCat (which is a stable function)


const recalcEnvRow = useCallback((row) => {
  const q = Number(row.quantity || 0);

  const carbon = row.carbon?.code
    ? {
        ...row.carbon,
        value: Number(row.carbon?.value || 0),
        total: Number(row.carbon?.value || 0) * q,
      }
    : { measurementId: '', code: '', unit: '', value: 0, total: 0 };

  const bio = row.bio?.code
    ? {
        ...row.bio,
        value: Number(row.bio?.value || 0),
        total: Number(row.bio?.value || 0) * q,
      }
    : { measurementId: '', code: '', unit: '', value: 0, total: 0 };

  const weight = row.weight?.code
    ? {
        ...row.weight,
        value: Number(row.weight?.value || 0),
        total: Number(row.weight?.value || 0) * q,
      }
    : { measurementId: '', code: '', unit: '', value: 0, total: 0 };

  const unit =
    row.unit ||
    carbon.unit ||
    bio.unit ||
    weight.unit ||
    '';

  return { ...row, quantity: q, unit, carbon, bio, weight };
}, []);


const recalcCostRow = useCallback((row) => {
  const desc = row.description || '';
  const q = row.quantity || 0;
  const hints = descToCodes[desc] || {};
  const rate  = fillForMetric(costCategories, desc, q, hints.cost);
  const unit = rate.unit || row.unit || '';
  return { ...row, unit, rate };
}, [costCategories, descToCodes, fillForMetric]);



  // Description chosen → set category automatically
  const onEnvDescChange = (id, desc) => {
    const cat = findCategoryForDesc(desc);
    updateEnvRow(id, { description: desc, category: cat });
  };
  const onCostDescChange = (id, desc) => {
    const cat = findCategoryForDesc(desc);
    updateCostRow(id, { description: desc, category: cat });
  };

  // Category chosen → keep description only if it belongs; otherwise clear it
  const onEnvCatChange = (id, cat) => {
    setRows(prev => prev.map(r => {
      if (r.id !== id) return r;
      const ok = r.description &&
        ENV_ALL.some(x => getName(x) === r.description && x.Category === cat);  // ✅ No parentheses
      const next = { ...r, category: cat, description: ok ? r.description : '' };
      return recalcEnvRow(next);
    }));
  };

  const onCostCatChange = (id, cat) => {
    setCostRows(prev => prev.map(r => {
      if (r.id !== id) return r;
      const ok = r.description &&
        costCategories.some(x => getName(x) === r.description && x.Category === cat);
      const next = { ...r, category: cat, description: ok ? r.description : '' };
      return recalcCostRow(next);
    }));
  };

  const loadNewMaterialCategories = async () => {
    try {
      const categories = await fetchNewMaterialCategories();
      setNewMaterialCategories(Array.isArray(categories) ? categories : []);
    } catch (error) {
      console.error('❌ Error fetching material categories:', error);
      setNewMaterialCategories([]);
    }
  };

  // ✅ NEW: Add heading row
  const addEnvHeading = () => {
    const newHeading = {
      id: uuidv4(),
      isHeading: true,
      heading: 'New Heading',
      category: '',
      description: '',
      quantity: 0,
      unit: '',
      carbon: {},
      bio: {},
      weight: {},
      locked: false
    };
    setRows(prev => [...prev, newHeading]);
  };

  // 🆕 addEnvSubHeading
  const addEnvSubHeading = () => {
    const newSubHeading = {
      id:           uuidv4(),
      isHeading:    false,
      isSubHeading: true,     // 🆕
      heading:      null,
      subHeading:   'New Sub-Heading',
      category:     '',
      description:  '',
      quantity:     0,
      unit:         '',
      carbon:       {},
      bio:          {},
      weight:       {},
      locked:       false,
    };
    setRows(prev => [...prev, newSubHeading]);
  };

  // ✅ NEW: Handle heading change
  const handleHeadingChange = (id, value) => {
    setRows(prev => prev.map(r => 
      r.id === id ? { ...r, heading: value } : r
    ));
  };

  // 🆕 handleSubHeadingChange
  const handleSubHeadingChange = (id, value) => {
    setRows(prev => prev.map(r =>
      r.id === id ? { ...r, subHeading: value } : r
    ));
  };

  // ✅ Drag-and-drop handlers for reordering rows
  const handleDragStart = (e, index) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', index.toString());
    e.currentTarget.style.opacity = '0.4';
  };

  const handleDragEnd = (e) => {
    e.currentTarget.style.opacity = '1';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    return false;
  };

  const handleDrop = (e, dropIndex) => {
    e.stopPropagation();
    e.preventDefault();

    const dragIndex = parseInt(e.dataTransfer.getData('text/html'));

    if (dragIndex === dropIndex) return;

    const updated = [...rows];
    const [draggedItem] = updated.splice(dragIndex, 1);
    updated.splice(dropIndex, 0, draggedItem);

    // Update heading / sub-heading associations after reorder
    let currentHeading    = null;
    let currentSubHeading = null;
    updated.forEach((item, idx) => {
      if (item.isHeading) {
        currentHeading    = item.heading;
        currentSubHeading = null;
      } else if (item.isSubHeading) {
        currentSubHeading = item.subHeading;
      } else {
        updated[idx] = { ...updated[idx], heading: currentHeading, subHeading: currentSubHeading };
      }
    });

    setRows([...updated]);
    console.log('🔄 Reordered items - moved from', dragIndex, 'to', dropIndex);
  };

  const addEnvRow = () => {
    const r = recalcEnvRow({ id: uuidv4(), category: '', description: '', quantity: 0, unit: '', carbon:{}, bio:{}, weight:{}, locked: false, isHeading: false, isSubHeading: false, heading: null, subHeading: null });
    setRows(prev => [...prev, r]);
  };
  const addCostRow = () => {
    const r = recalcCostRow({ id: uuidv4(), category: '', description: '', quantity: 0, unit: '', rate:{}, locked: false, isHeading: false, isSubHeading: false, heading: null, subHeading: null });
    setCostRows(prev => [...prev, r]);
  };

  const deleteEnvRow = (id) => setRows(prev => prev.filter(r => r.id !== id));

  // "+ Add Assembly": an approved assembly's build-up (read the same way Load Template reads it) …
  const loadAssemblyBuildUp = useCallback(async (materialId) => {
    const bd = await fetchBreakdownData(materialId);
    const raw = bd?.data?.data || bd?.data || bd || [];
    const li = await api.get(`/line-items/${materialId}`);
    return { breakdown: Array.isArray(raw) ? raw : [], lineItems: li.data?.data?.lineItems || [] };
  }, []);
  // … copied in under its own heading (AddAssemblyModal builds the rows)
  const addAssemblyRows = ({ rows: newRows, costRows: newCost, rowHistory: newHistory }) => {
    setRows(prev => [...prev, ...newRows]);
    setCostRows(prev => [...prev, ...newCost]);
    setRowHistory(prev => ({ ...prev, ...newHistory }));
  };
  const applyBlockChange = (next) => {
    setRows(next.rows);
    setCostRows(next.costRows);
    setRowHistory(next.rowHistory);
  };
  const removeAssembly = (blockId) => applyBlockChange(removeAssemblyBlock({ rows, costRows, rowHistory }, blockId));
  const rescaleAssembly = (blockId, scale) => applyBlockChange(rescaleAssemblyBlock({ rows, costRows, rowHistory }, blockId, scale));
  // "My approved materials" in the Add Assembly search: the user's own approved requests (public or
  // private) plus private materials they own — fetched each time the search opens
  const [myApprovedIds, setMyApprovedIds] = useState(() => new Set());
  useEffect(() => {
    if (!addAssemblyOpen || isDenied) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const token = await auth.currentUser?.getIdToken();
        const res = await api.get('/composite-requests/all', {
          headers: {
            Authorization: `Bearer ${token}`,
            'x-region': selectedRegion,
            'x-user-role': role,
            'x-user-id': auth.currentUser?.uid || '',
            'x-user-email': auth.currentUser?.email || '',
          },
        });
        const me = String(auth.currentUser?.email || '').toLowerCase();
        const ids = (res.data?.requests || [])
          .filter(r => ['Approved', 'Completed'].includes(r.Status) && r.QuantityUnitId
            && String(r.SubmittedByEmail || '').toLowerCase() === me)
          .map(r => r.QuantityUnitId);
        if (!cancelled) setMyApprovedIds(new Set(ids));
      } catch {
        // the tab still lists the private materials you own
      }
    })();
    return () => { cancelled = true; };
  }, [addAssemblyOpen, isDenied, selectedRegion, role]);
  const isMyMaterial = useCallback((m) => {
    const me = String(auth.currentUser?.email || '').toLowerCase();
    return myApprovedIds.has(m.Id) || (!!m.PrivateOwnerEmail && String(m.PrivateOwnerEmail).toLowerCase() === me);
  }, [myApprovedIds]);

  // the request tells its reviewer which assemblies it was built from
  const commentsForRequest = () => [String(userComments || '').trim(), includesNote(rows)].filter(Boolean).join('\n\n');
  const deleteCostRow = (id) => setCostRows(prev => prev.filter(r => r.id !== id));

  const updateEnvRow = (id, patch) => {
    setRows(prev => {
      const updatedRows = prev.map(r => (r.id === id ? recalcEnvRow({ ...r, ...patch }) : r));

      const updatedRow = updatedRows.find(r => r.id === id);
      if (updatedRow && !updatedRow.isHeading) {
        setCostRows(prevCost =>
          prevCost.map(cr => {
            if (cr.envRowId !== updatedRow.id) return cr;

            const value = Number(cr.rate?.value || 0);
            const quantity = Number(updatedRow.quantity || 0);

            return {
              ...cr,
              description: updatedRow.description,
              category: updatedRow.category,
              quantity,
              heading: updatedRow.heading || cr.heading || null,
              rate: {
                ...cr.rate,
                total: value * quantity,
              },
            };
          })
        );
      }

      return updatedRows;
    });
  };
  const updateCostRow = (id, patch) => setCostRows(prev => prev.map(r => (r.id === id ? recalcCostRow({ ...r, ...patch }) : r)));

  const onEnvQtyChange  = (id, raw) => {
    let q = raw;
    try { if (String(raw).startsWith('=')) q = evaluate(String(raw).slice(1)); } catch {}
    updateEnvRow(id, { quantity: Number(q) || 0 });
  };
  const onCostQtyChange = (id, raw) => {
    let q = raw;
    try { if (String(raw).startsWith('=')) q = evaluate(String(raw).slice(1)); } catch {}
    updateCostRow(id, { quantity: Number(q) || 0 });
  };

  // Template materials for the Load Template picker (the same SmartMaterialSearch + Search Materials
  // modal as the line items): grouped by their Nezo category, searchable by name, category or id, and
  // keyed by id so two templates with the same name (e.g. a private copy) both stay listed.
  const templateOptions = useMemo(() => {
    const catName = Object.fromEntries((NewMaterialCategories || []).map(c => [String(c.id), c.name]));
    return (materials || []).map(m => ({
      ...m,
      Name: m.PrivateOwnerEmail ? `${m.Name} (private)` : m.Name,
      Category: catName[String(m.CategoryId)] || 'Other',
      SearchKeys: m.Id,
      DedupeKey: m.Id,
    }));
  }, [materials, NewMaterialCategories]);
  const selectedTemplate = templateOptions.find(m => m.Id === selectedMaterial);

  // ✅ UPDATED: Load base material with headings
 const loadBaseMaterial = useCallback(
  async (materialId, catCarbon, catBiogenic, catWeight) => {
    if (!materialId || isDenied) return;
    try {
      setLoading(true);

      // 1️⃣ Fetch breakdown data
      const breakdownData = await fetchBreakdownData(materialId);
      console.log('🔍 Breakdown response:', breakdownData);
      const responseData = breakdownData.data?.data || breakdownData.data || breakdownData || [];
      const data = Array.isArray(responseData) ? responseData : [];
      console.log('✅ Final data array:', data.length, 'items');

      // 2️⃣ ✅ Fetch line items WITH HEADINGS
      console.log('🔍 Fetching line items for material:', materialId);
      const lineItemsResponse = await api.get(`/line-items/${materialId}`);
      const lineItemsData = lineItemsResponse.data?.data?.lineItems || [];
      
      console.log('✅ Loaded line items:', lineItemsData.length);
      if (lineItemsData.length > 0) {
        console.log('📊 Sample line item:', lineItemsData[0]);
      }

      // 3️⃣ the template's build-up → rows. Shared with "+ Add Assembly" (utils/expandAssembly), which
      // copies an assembly in scaled under its own heading instead of replacing the rows.
      const built = breakdownToRows({ breakdown: data, lineItems: lineItemsData, uuid: uuidv4, categoryFor: findCategoryForDesc });
      const envRows = built.rows;
      const costList = built.costRows;
      setRows(envRows);
      setCostRows(costList);
      setRowHistory(built.rowHistory);

      if (envRows.length > 0 || costList.length > 0) {
        setSuccess('Base material loaded. You can now tweak rows and submit a request.');
      } else {
        setError('Material loaded but no line items found. The breakdown data may be empty.');
      }
    } catch (e) {
      console.error('[BuildNewCompositePage] ❌ Error in loadBaseMaterial:', e);
      setError(`Failed to load base material: ${e.message}`);
    } finally {
      setLoading(false);
    }
  },
  [isDenied, recalcEnvRow, recalcCostRow, findCategoryForDesc, carbonCategories, biogenicCategories, weightCategories]
);

  // After materials load, prefill from /composite-requests navigation state.
  useEffect(() => {
    if (location.state?.copyRequest) return; // a full copy, seeded by the effect below
    if (seeded) return;
    if (!materials?.length) return;
    if (!carbonCategories.length) return;
    if (!regions.length) return; 

    const stateData = location.state?.prefill || location.state;
    const urlMaterialId = searchParams.get('materialId');
    const urlMaterialName = searchParams.get('materialName');
    
    const prefill = stateData || (urlMaterialId ? {
      materialId: urlMaterialId,
      materialName: urlMaterialName ? decodeURIComponent(urlMaterialName) : null
    } : null);
    
    if (!prefill) return;

    console.log('[BuildNewCompositePage] 🎯 Prefill data received:', prefill);
    console.log('[BuildNewCompositePage] 🔗 From URL params:', { urlMaterialId, urlMaterialName });

    let match = null;
    
    // Try to find by materialId first
    if (prefill.materialId) {
      match = materials.find(m => String(m.Id) === String(prefill.materialId));
      console.log('[BuildNewCompositePage] 🔍 Material ID search:', prefill.materialId, 'Found:', !!match);
    }
    
    // If not found, try by name (exact match)
    if (!match && prefill.materialName) {
      const q = String(prefill.materialName).trim().toLowerCase();
      match = materials.find(m => m.Name?.toLowerCase() === q);
      console.log('[BuildNewCompositePage] 🔍 Material Name search (exact):', q, 'Found:', !!match);
      
      // Fallback to contains
      if (!match) {
        match = materials.find(m => m.Name?.toLowerCase().includes(q));
        console.log('[BuildNewCompositePage] 🔍 Material Name search (contains):', q, 'Found:', !!match);
      }
    }
    
    if (match) {
      console.log('[BuildNewCompositePage] ✅ Material matched:', match);
      setSearchQuery(match.Name);
      setFilteredMaterials([match, ...materials.filter(m => m.Id !== match.Id)]);
      setSelectedMaterial(match.Id);
      
      // Load the breakdown data
      (async () => { 
        await loadBaseMaterial(match.Id, carbonCatRef.current, biogenicCatRef.current, weightCatRef.current);
        console.log('[BuildNewCompositePage] ✅ Base material loaded for:', match.Name);
      })();
      
      setSeeded(true);
    } else {
      // If material not found in list, load it directly by ID
      if (prefill.materialId) {
        console.log('[BuildNewCompositePage] ⚠️ Material not in list, loading directly:', prefill.materialId);
        
        // Set custom name/description to indicate it's a prefilled material
        if (prefill.materialName) {
          setCustomMaterialName(prefill.materialName);
          console.log('[BuildNewCompositePage] 📝 Set custom name:', prefill.materialName);
        }
        
        // Directly load the breakdown without selecting from the dropdown
        (async () => {
          try {
            await loadBaseMaterial(prefill.materialId, carbonCatRef.current, biogenicCatRef.current, weightCatRef.current);
            console.log('[BuildNewCompositePage] ✅ Base material loaded directly for ID:', prefill.materialId);
            
            // Show success message
            setTimeout(() => {
              setSuccess(`Material "${prefill.materialName || prefill.materialId}" loaded! Base data shown below. You can now tweak line items and submit.`);
            }, 500);
          } catch (error) {
            console.error('[BuildNewCompositePage] ❌ Failed to load material:', error);
            setError(`Failed to load material: ${error.message}`);
          }
        })();
        
        setSeeded(true);
      } else {
        console.log('[BuildNewCompositePage] ❌ No match found for prefill data');
      }
    }
  }, [materials, carbonCategories, regions, location.state, searchParams, loadBaseMaterial, seeded]);

  // NEW: Handle custom material name change
  // "Copy material" (My Materials): seed Create New Material with the WHOLE request — after the lists for
  // its region have loaded (the region-change reset above clears the form on mount, so this must follow it).
  const copyRequest = location.state?.copyRequest;
  useEffect(() => {
    if (!copyRequest || copySeededRef.current) return;
    if (loading || !NewMaterialCategories.length) return;
    if (copyRequest.regionId && copyRequest.regionId !== selectedRegion) return;
    copySeededRef.current = true;
    setSeeded(true);

    const { rows: copiedRows, costRows: copiedCost, rowHistory: copiedHistory } =
      rowsFromLineItems(copyRequest.lineItems, uuidv4);
    setMaterialTab('create');
    setSelectedMaterial('');
    setCustomMaterialName(copyRequest.name || '');
    setCustomMaterialDescription(copyRequest.description || '');
    setCustomMaterialUnit(copyRequest.unit || '');
    setCustomMaterialCategoryId(resolveCopyCategory(copyRequest.categoryId, NewMaterialCategories));
    setVisibility(copyRequest.visibility || 'private');
    setCostOnlyChecked(!!copyRequest.costOnly);
    setRows(copiedRows);
    setCostRows(copiedCost);
    setRowHistory(copiedHistory);
    setCopySourceName(copyRequest.sourceName || copyRequest.name || '');
    setError(null);
    setSuccess(`Copied "${copyRequest.name}". Give it a new name, check the line items, then submit.`);
    // drop the router state so a reload or "Build another" starts empty
    navigate(location.pathname, { replace: true, state: null });
  }, [copyRequest, loading, NewMaterialCategories, selectedRegion, navigate, location.pathname]);

  // the copy must be renamed before it goes anywhere
  const copyNeedsRename = isUnrenamedCopy(customMaterialName, copySourceName);
  const refuseUnrenamedCopy = () => {
    if (!copyNeedsRename) return false;
    alert(`Give the copy a new name — it still has the name of the material it was copied from ("${copySourceName}").`);
    setMaterialTab('create');
    return true;
  };

  const onCustomNameChange = (e) => {
    setCustomMaterialName(e.target.value);
  };

  // NEW: Handle custom material description change
  const onCustomDescriptionChange = (e) => {
    setCustomMaterialDescription(e.target.value);
  };

  // ✅ ADD THIS NEW FUNCTION HERE:
  const onSelectMaterial = async (val) => {
    console.log('[BuildNewCompositePage] 📋 Material selected from dropdown:', val);
    
    setSelectedMaterial(val);
    
    if (val) {
      // Clear custom name/description when selecting from dropdown
      setCustomMaterialName('');
      setCustomMaterialDescription('');
      
      // Load the material's breakdown data
      try {
        await loadBaseMaterial(val, carbonCatRef.current, biogenicCatRef.current, weightCatRef.current);
        console.log('[BuildNewCompositePage] ✅ Material loaded successfully');
      } catch (error) {
        console.error('[BuildNewCompositePage] ❌ Failed to load material:', error);
        setError(`Failed to load material: ${error.message}`);
      }
    } else {
      // Material deselected - clear rows
      setRows([]);
      setCostRows([]);
      setRowHistory({});
      setDescToCodes({});
    }
  };

  // NEW: Handle Next button click
  const handleNextClick = () => {
    // Just show the popup immediately
    setShowNextPopup(true);
  };

   // NEW: Handle "Got it!" click - scroll to Environmental Line Items
  const handleGotItClick = () => {
    setShowNextPopup(false);
    
    // Wait for popup to close, then scroll to Environmental Line Items
    setTimeout(() => {
      const envSection = document.getElementById('environmental-line-items');
      if (envSection) {
        envSection.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      } else {
        // Fallback: scroll down by a reasonable amount
        const scrollPosition = window.innerHeight * 0.8;
        window.scrollBy({
          top: scrollPosition,
          behavior: 'smooth'
        });
      }
    }, 100);
  };

  // totals
  const totals = useMemo(() => {
    const env = rows.filter(r => !r.isHeading && !r.isSubHeading).reduce((acc, r) => {
      acc.carbon  += r.carbon?.total  || 0;
      acc.biogenic+= r.bio?.total     || 0;
      acc.weight  += r.weight?.total  || 0;
      acc.uCarbon  ||= r.carbon?.unit;
      acc.uBio     ||= r.bio?.unit;
      acc.uWeight  ||= r.weight?.unit;
      return acc;
    }, { carbon:0, biogenic:0, weight:0, uCarbon:'', uBio:'', uWeight:'' });

    const cost = costRows.reduce((acc, r) => {
      acc.cost += r.rate?.total || 0;
      acc.uCost ||= r.rate?.unit;
      return acc;
    }, { cost:0, uCost:'' });

    return { ...env, ...cost };
  }, [rows, costRows]);

  // effective only for a brand-new material (the template tab always carries a build-up)
  const costOnly = costOnlyChecked && materialTab === 'create';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isDenied) { alert('Only users can submit material requests.'); return; }
    if (!canUpload) { alert("Your company's reviewers or admins upload materials to Nezo. Save as Draft or Send for Internal Review instead."); return; }
    // Only check Environmental rows since Cost rows are hidden — a cost-only material has none by design
    if (refuseUnrenamedCopy()) return;
    if (!costOnly && rows.filter(r => !r.isHeading && !r.isSubHeading).length === 0) return alert('Add at least one environmental line item, or tick "Cost only" on the Create New Material tab.');
    if (costOnly && !customMaterialUnit) {
      alert('Please choose a unit for a cost-only material — its Rate Library cost is priced per unit.');
      setMaterialTab('create');
      return;
    }

    if (!customMaterialCategoryId) {
      alert('Please select a Category ID before submitting.');
      setMaterialTab('create');
      return;
    }

    setSubmitting(true);
    try {
      // ✅ GET FIREBASE TOKEN FIRST
      const token = await auth.currentUser.getIdToken();

      const firstEnvUnit  = rows.find(r => r.unit)?.unit || null;
      const firstCostUnit = costRows.find(r => r.unit)?.unit || null;
      const compositeUnit = firstEnvUnit || firstCostUnit || null;
      
      // ✅ UPDATED: env -> 3 per row, track heading from rows
      let currentHeading    = null;
      let currentSubHeading = null;   // 🆕
 
      const envLineItems = rows.flatMap((r) => {
        if (r.isHeading) {
          currentHeading    = r.heading;
          currentSubHeading = null;    // 🆕 reset on new heading
          return [];
        }
        if (r.isSubHeading) {
          currentSubHeading = r.subHeading;  // 🆕
          return [];
        }
 
        const packs = [
          { baseId: TYPE_BASE.CO2,    pack: r.carbon },
          { baseId: TYPE_BASE.BIO,    pack: r.bio    },
          { baseId: TYPE_BASE.WEIGHT, pack: r.weight },
        ];
        return packs
          .filter(x => x.pack && (x.pack.code || x.pack.measurementId))
          .map(x => ({
            id:            uuidv4(),
            measurementId: x.pack.measurementId || `vquest-${x.baseId}`,
            description:   r.description,
            category:      r.category || '',
            code:          x.pack.code || '',
            quantity:      r.quantity,
            unit:          x.pack.unit || '',
            value:         x.pack.value || 0,
            total:         x.pack.total || 0,
            line_item_rows:rowHistory[r.id] || [],
            isHeading:     false,
            isSubHeading:  false,
            heading:       currentHeading,    // parent heading
            sub_heading:   currentSubHeading, // 🆕 parent sub-heading
            user_added:    !r.locked || (rowHistory[r.id]?.length > 0),
            ...(r.assemblyBlock ? { assembly_source: r.assemblyBlock } : {}),
          }));
      });

      // ✅ Add extensive debugging to the cost line items section
      console.log('🔍 DEBUG: Building cost line items');
      console.log('📊 Cost rows:', costRows.length);
      console.log('📊 ENV rows:', rows.length);

      // Log cost rows structure
      costRows.forEach((r, idx) => {
        console.log(`💰 Cost row ${idx}:`, {
          description: r.description?.substring(0, 30),
          heading: r.heading,
          hasRate: !!r.rate,
          hasCode: !!(r.rate?.code || r.rate?.measurementId)
        });
      });

      // Try to match with ENV rows
      costRows.forEach((r, idx) => {
        const matchingEnvRow = rows.find(envRow => 
          !envRow.isHeading && envRow.description === r.description
        );
        console.log(`🔍 Cost row ${idx} matching:`, {
          costDesc: r.description?.substring(0, 30),
          matchFound: !!matchingEnvRow,
          matchHeading: matchingEnvRow?.heading,
          envRowsChecked: rows.filter(r => !r.isHeading).length
        });
      });

      // ✅ FIXED: cost -> 1 per row WITH INHERITED HEADINGS
       const costLineItems = costRows
        .filter(r => r.rate && (r.rate.code || r.rate.measurementId))
        .map(r => {
          const matchingEnvRow = rows.find(envRow =>
            !envRow.isHeading && !envRow.isSubHeading && envRow.description === r.description
          );
          const finalHeading    = matchingEnvRow?.heading    || r.heading    || null;
          const finalSubHeading = matchingEnvRow?.subHeading || r.subHeading || null; // 🆕
 
          return {
            id:            uuidv4(),
            envRowId:      r.envRowId || null,
            measurementId: r.rate.measurementId || `vquest-${TYPE_BASE.RATE}`,
            description:   r.description,
            category:      r.category || matchingEnvRow?.category || '',
            code:          r.rate.code || '',
            quantity:      r.quantity,
            unit:          r.rate.unit || '',
            value:         r.rate.value || 0,
            total:         r.rate.total || 0,
            line_item_rows:[],
            isHeading:     false,
            isSubHeading:  false,
            heading:       finalHeading,
            sub_heading:   finalSubHeading,  // 🆕
            user_added:    !r.locked,
            ...(r.assemblyBlock ? { assembly_source: r.assemblyBlock } : {}),
          };
        });

      console.log('✅ Cost line items built:', costLineItems.length);
      console.log('📋 Cost headings:', costLineItems.map(c => ({
        desc: c.description?.substring(0, 30),
        heading: c.heading
      })));



      // 🔹 Minimal material meta so the backend accepts the request.
      const base = materials.find(m => String(m.Id) === String(selectedMaterial)) || {};

       // NEW: Use custom name if provided, otherwise use base material name or default
      const materialName = customMaterialName.trim() || base.Name || 'Composite Request';
      
      // NEW: Use custom description if provided, otherwise generate default
      let materialDescription;
      if (customMaterialDescription.trim()) {
        materialDescription = customMaterialDescription.trim();
      } else if (customMaterialName.trim()) {
        materialDescription = `Custom material: "${materialName}" (${regionName})`;
      } else {
        materialDescription = `Update request for "${base.Name || 'Unknown'}" (${regionName})`;
      }

      const material = {
        Id: uuidv4(),
        BaseMaterialId: selectedMaterial,
        Name: materialName,
        Description: materialDescription,
        ColorHex: '#000000',
        QuantityType: customMaterialUnit || base.QuantityTypeName || base.QuantityType || compositeUnit || '',
        CategoryId: customMaterialCategoryId || base.CategoryId || null,
        // read by validateForAutoApprove: a cost-only material legitimately has no breakdown lines
        ...(costOnly ? { CostOnly: true } : {}),
      };

      const requestData = {
        material,
        lineItems: costOnly ? [] : [...envLineItems, ...costLineItems],
        visibility, 
        EnvironmentalData: {
          carbon:   { value: totals.carbon,   unit: totals.uCarbon  },
          biogenic: { value: totals.biogenic, unit: totals.uBio     },
          weight:   { value: totals.weight,   unit: totals.uWeight  },
          cost:     { value: totals.cost,     unit: totals.uCost    },
          _userComments: commentsForRequest()
        },
        userComments: commentsForRequest(),
        regionId: selectedRegion,
        regionName,
        submittedBy: {
          uid: auth.currentUser?.uid || 'unknown',
          email: auth.currentUser?.email || 'unknown',
          role,
        },
        submittedAt: new Date().toISOString(),
      };

      dlog('Submitting as role:', role);

      // ✅ ADD THIS DEBUG LOGGING
      console.log('🚀 Final request data before submission:');
      console.log('📊 Total line items:', requestData.lineItems.length);
      console.log('💰 Cost items in payload:', 
        requestData.lineItems
          .filter(li => li.measurementId?.includes('Rate'))
          .map(li => ({
            desc: li.description?.substring(0, 30),
            heading: li.heading,
            measurementId: li.measurementId
          }))
      );

      const resp = await fetch(`${API_BASE_URL}/composite-requests`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'x-region': selectedRegion,
          'X-User-ID': auth.currentUser?.uid || 'unknown',
          'X-User-Email': auth.currentUser?.email || 'unknown',
          'X-User-Role': role,
        },
        body: JSON.stringify(requestData),
      });

      if (!resp.ok) throw new Error(await resp.text() || 'Failed to submit');

      const result = await resp.json();
      setThanksId(result.requestId);
      setThanksInfo({ visibility: result.visibility || visibility, autoApproved: !!result.autoApproved, costOnly });
      setShowThanks(true);
      setSuccess(`Material request submitted! Request ID: ${result.requestId}`);
      setRows([]); setCostRows([]); setRowHistory({});
      setUserComments(''); setSelectedMaterial(''); setSearchQuery('');
      setCustomMaterialName('');
      setCustomMaterialDescription('');
      setCustomMaterialCategoryId('');
      setCustomMaterialUnit('');
      setVisibility('public'); 
      logAction(auth.currentUser?.uid || 'unknown', auth.currentUser?.email || 'unknown',
        'SUBMIT_MATERIAL_REQUEST', { requestId: result.requestId, regionId: selectedRegion, role });
    } catch (e) {
      console.error(e);
      setError(String(e.message || e));
    } finally {
      setSubmitting(false);
      setTimeout(() => setSuccess(''), 5000);
    }
  };

  const handleSaveDraft = async () => {
    if (isDenied) { alert('Only users can save drafts.'); return; }
    if (refuseUnrenamedCopy()) return;

    if (!customMaterialCategoryId) {
      alert('Please select a Category ID before saving.');
      setMaterialTab('create');
      return;
    }
 
    setSubmitting(true);
    try {
      const token = await auth.currentUser.getIdToken();
 
      const firstEnvUnit  = rows.find(r => r.unit)?.unit || null;
      const firstCostUnit = costRows.find(r => r.unit)?.unit || null;
      const compositeUnit = firstEnvUnit || firstCostUnit || null;
 
      // Build line items (same logic as handleSubmit)
      let currentHeading    = null;
      let currentSubHeading = null;  // 🆕
 
      const envLineItems = rows.flatMap((r) => {
        if (r.isHeading)    { currentHeading = r.heading; currentSubHeading = null; return []; }
        if (r.isSubHeading) { currentSubHeading = r.subHeading; return []; }  // 🆕
 
        const packs = [
          { baseId: TYPE_BASE.CO2,    pack: r.carbon },
          { baseId: TYPE_BASE.BIO,    pack: r.bio    },
          { baseId: TYPE_BASE.WEIGHT, pack: r.weight },
        ];
        return packs
          .filter(x => x.pack && (x.pack.code || x.pack.measurementId))
          .map(x => ({
            id:            uuidv4(),
            envRowId:      r.envRowId || null,
            measurementId: x.pack.measurementId || `vquest-${x.baseId}`,
            description:   r.description,
            category:      r.category || '',
            code:          x.pack.code || '',
            quantity:      r.quantity,
            unit:          x.pack.unit || '',
            value:         x.pack.value || 0,
            total:         x.pack.total || 0,
            line_item_rows:rowHistory[r.id] || [],
            isHeading:     false,
            isSubHeading:  false,
            heading:       currentHeading,
            sub_heading:   currentSubHeading,  // 🆕
            user_added:    !r.locked || (rowHistory[r.id]?.length > 0),
            ...(r.assemblyBlock ? { assembly_source: r.assemblyBlock } : {}),
          }));
      });
 
      const costLineItems = costRows
        .filter(r => r.rate && (r.rate.code || r.rate.measurementId))
        .map(r => {
          const matchingEnvRow = rows.find(e =>
            !e.isHeading && !e.isSubHeading && e.description === r.description
          );
          return {
            id:            uuidv4(),
            measurementId: r.rate.measurementId || `vquest-${TYPE_BASE.RATE}`,
            description:   r.description,
            category:      r.category || matchingEnvRow?.category || '',
            code:          r.rate.code || '',
            quantity:      r.quantity,
            unit:          r.rate.unit || '',
            value:         r.rate.value || 0,
            total:         r.rate.total || 0,
            line_item_rows:[],
            isHeading:     false,
            isSubHeading:  false,
            heading:       matchingEnvRow?.heading    || r.heading    || null,
            sub_heading:   matchingEnvRow?.subHeading || r.subHeading || null,  // 🆕
            user_added:    !r.locked,
            ...(r.assemblyBlock ? { assembly_source: r.assemblyBlock } : {}),
          };
        });
 
      const base = materials.find(m => String(m.Id) === String(selectedMaterial)) || {};
      const materialName = customMaterialName.trim() || base.Name || 'Draft';
      const materialDescription = customMaterialDescription.trim()
        || (customMaterialName.trim()
            ? `Draft: "${materialName}" (${regionName})`
            : `Draft for "${base.Name || 'Unknown'}" (${regionName})`);
 
      const material = {
        Id: uuidv4(),
        BaseMaterialId: selectedMaterial,
        Name: materialName,
        Description: materialDescription,
        ColorHex: '#000000',
        QuantityType: customMaterialUnit || base.QuantityTypeName || base.QuantityType || compositeUnit || '',
        CategoryId: customMaterialCategoryId || base.CategoryId || null,
        ...(costOnly ? { CostOnly: true } : {}),
      };
 
      const requestData = {
        material,
        lineItems: [...envLineItems, ...costLineItems],
        visibility,
        status: 'Draft',        // ← tells the backend not to notify admins
        EnvironmentalData: {
          carbon:   { value: totals.carbon,   unit: totals.uCarbon  },
          biogenic: { value: totals.biogenic, unit: totals.uBio     },
          weight:   { value: totals.weight,   unit: totals.uWeight  },
          cost:     { value: totals.cost,     unit: totals.uCost    },
          _userComments: commentsForRequest(),
        },
        userComments: commentsForRequest(),
        regionId: selectedRegion,
        regionName,
        submittedBy: {
          uid:   auth.currentUser?.uid   || 'unknown',
          email: auth.currentUser?.email || 'unknown',
          role,
        },
        submittedAt: new Date().toISOString(),
      };
 
      const resp = await fetch(`${API_BASE_URL}/composite-requests`, {
        method: 'POST',
        headers: {
          'Content-Type':  'application/json',
          'Authorization': `Bearer ${token}`,
          'x-region':      selectedRegion,
          'X-User-ID':     auth.currentUser?.uid   || 'unknown',
          'X-User-Email':  auth.currentUser?.email || 'unknown',
          'X-User-Role':   role,
        },
        body: JSON.stringify(requestData),
      });
 
      if (!resp.ok) throw new Error(await resp.text() || 'Failed to save draft');
 
      const result = await resp.json();
      setShowDraftSaved(true);
 
      logAction(
        auth.currentUser?.uid   || 'unknown',
        auth.currentUser?.email || 'unknown',
        'SAVE_DRAFT_MATERIAL_REQUEST',
        { requestId: result.requestId, regionId: selectedRegion, role }
      );
    } catch (e) {
      console.error(e);
      setError(String(e.message || e));
    } finally {
      setSubmitting(false);
      setTimeout(() => setSuccess(''), 6000);
    }
  };

  const handleSendForReview = async () => {
    if (isDenied) { alert('Only users can send requests for review.'); return; }
    if (refuseUnrenamedCopy()) return;

    if (!customMaterialCategoryId) {
      alert('Please select a Category ID before sending for review.');
      setMaterialTab('create');
      return;
    }
 
    setSubmitting(true);
    try {
      const token = await auth.currentUser.getIdToken();
 
      const firstEnvUnit  = rows.find(r => r.unit)?.unit || null;
      const firstCostUnit = costRows.find(r => r.unit)?.unit || null;
      const compositeUnit = firstEnvUnit || firstCostUnit || null;
 
      let currentHeading    = null;
      let currentSubHeading = null;
 
      const envLineItems = rows.flatMap((r) => {
        if (r.isHeading)    { currentHeading = r.heading; currentSubHeading = null; return []; }
        if (r.isSubHeading) { currentSubHeading = r.subHeading; return []; }
 
        const packs = [
          { baseId: TYPE_BASE.CO2,    pack: r.carbon },
          { baseId: TYPE_BASE.BIO,    pack: r.bio    },
          { baseId: TYPE_BASE.WEIGHT, pack: r.weight },
        ];
        return packs
          .filter(x => x.pack && (x.pack.code || x.pack.measurementId))
          .map(x => ({
            id:            uuidv4(),
            envRowId:      r.envRowId || null,
            measurementId: x.pack.measurementId || `vquest-${x.baseId}`,
            description:   r.description,
            category:      r.category || '',
            code:          x.pack.code || '',
            quantity:      r.quantity,
            unit:          x.pack.unit || '',
            value:         x.pack.value || 0,
            total:         x.pack.total || 0,
            line_item_rows: rowHistory[r.id] || [],
            isHeading:     false,
            isSubHeading:  false,
            heading:       currentHeading,
            sub_heading:   currentSubHeading,
            user_added:    !r.locked || (rowHistory[r.id]?.length > 0),
            ...(r.assemblyBlock ? { assembly_source: r.assemblyBlock } : {}),
          }));
      });
 
      const costLineItems = costRows
        .filter(r => r.rate && (r.rate.code || r.rate.measurementId))
        .map(r => {
          const matchingEnvRow = rows.find(e =>
            !e.isHeading && !e.isSubHeading && e.description === r.description
          );
          return {
            id:            uuidv4(),
            measurementId: r.rate.measurementId || `vquest-${TYPE_BASE.RATE}`,
            description:   r.description,
            category:      r.category || matchingEnvRow?.category || '',
            code:          r.rate.code || '',
            quantity:      r.quantity,
            unit:          r.rate.unit || '',
            value:         r.rate.value || 0,
            total:         r.rate.total || 0,
            line_item_rows: [],
            isHeading:     false,
            isSubHeading:  false,
            heading:       matchingEnvRow?.heading    || r.heading    || null,
            sub_heading:   matchingEnvRow?.subHeading || r.subHeading || null,
            user_added:    !r.locked,
            ...(r.assemblyBlock ? { assembly_source: r.assemblyBlock } : {}),
          };
        });
 
      const base = materials.find(m => String(m.Id) === String(selectedMaterial)) || {};
      const materialName = customMaterialName.trim() || base.Name || 'For Review';
      const materialDescription = customMaterialDescription.trim()
        || (customMaterialName.trim()
            ? `For Review: "${materialName}" (${regionName})`
            : `For Review: "${base.Name || 'Unknown'}" (${regionName})`);
 
      const material = {
        Id:           uuidv4(),
        BaseMaterialId: selectedMaterial,
        Name:         materialName,
        Description:  materialDescription,
        ColorHex:     '#000000',
        QuantityType: customMaterialUnit || base.QuantityTypeName || base.QuantityType || compositeUnit || '',
        CategoryId:   customMaterialCategoryId || base.CategoryId || null,
        ...(costOnly ? { CostOnly: true } : {}),
      };
 
      const requestData = {
        material,
        lineItems: [...envLineItems, ...costLineItems],
        visibility,
        status: 'ForReview',   // ← key difference from Draft
        EnvironmentalData: {
          carbon:   { value: totals.carbon,   unit: totals.uCarbon  },
          biogenic: { value: totals.biogenic, unit: totals.uBio     },
          weight:   { value: totals.weight,   unit: totals.uWeight  },
          cost:     { value: totals.cost,     unit: totals.uCost    },
          _userComments: commentsForRequest(),
        },
        userComments: commentsForRequest(),
        regionId: selectedRegion,
        regionName,
        submittedBy: {
          uid:   auth.currentUser?.uid   || 'unknown',
          email: auth.currentUser?.email || 'unknown',
          role,
        },
        submittedAt: new Date().toISOString(),
      };
 
      const resp = await fetch(`${API_BASE_URL}/composite-requests`, {
        method: 'POST',
        headers: {
          'Content-Type':  'application/json',
          'Authorization': `Bearer ${token}`,
          'x-region':      selectedRegion,
          'X-User-ID':     auth.currentUser?.uid   || 'unknown',
          'X-User-Email':  auth.currentUser?.email || 'unknown',
          'X-User-Role':   role,
        },
        body: JSON.stringify(requestData),
      });
 
      if (!resp.ok) throw new Error(await resp.text() || 'Failed to send for review');
 
      setShowForReviewSaved(true);
 
      logAction(
        auth.currentUser?.uid   || 'unknown',
        auth.currentUser?.email || 'unknown',
        'SEND_FOR_REVIEW_MATERIAL_REQUEST',
        { regionId: selectedRegion, role }
      );
    } catch (e) {
      console.error(e);
      setError(String(e.message || e));
    } finally {
      setSubmitting(false);
      setTimeout(() => setSuccess(''), 6000);
    }
  };

  // ---------- RENDER ----------
  if (isDenied) {
    return (
      <div className="form-section" style={{ textAlign: 'center' }}>
        <h2>Access restricted</h2>
        <p>This page is only available to end-users. Your role is <b>{role}</b>.</p>
      </div>
    );
  }

  if (loading) {
  return (
    <div className="rate-builder-page">
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading Material Data...</p>
      </div>
    </div>
  );
}


  return (
    <div className="rate-builder-page bnc-page">
      {/* Two columns so the material form sits beside the page info instead of below it — the
          line items start higher, with less scrolling. Stacks to one column on narrow screens. */}
      <div className="bnc-top">
      <div className="page-title-section bnc-top__info">
        <h1>Build New Material</h1>
        <div className="region-info">
          <span className="badge badge--region">Region: {regionName}</span>
          <span className="badge badge--role">{role.charAt(0).toUpperCase() + role.slice(1)}</span>
        </div>
        <p className="page-description">
          Pick a material, tweak its line items, and submit a request. Or Create a new material with your own selections.
        </p>
        <div className="bnc-howto">
          <p className="bnc-howto__title">How this works</p>
          <ol className="bnc-howto__steps">
            <li>
              <span className="bnc-howto__num">1</span>
              <div>
                <strong>Start from a template</strong> <em>(optional)</em>
                <span><b>Load Template Material</b> copies an existing material's build-up for you to tweak.</span>
              </div>
            </li>
            <li>
              <span className="bnc-howto__num">2</span>
              <div>
                <strong>Describe your material</strong>
                <span>On <b>Create New Material</b>, give it a new name, unit, description and category, and pick a
                  full build-up or cost only.</span>
              </div>
            </li>
            <li>
              <span className="bnc-howto__num">3</span>
              <div>
                <strong>Add the line items</strong>
                <span>Add its carbon, biogenic and weight line items below — or whole approved assemblies with
                  <b> + Add Assembly</b>. Not needed for cost only.</span>
              </div>
            </li>
            <li>
              <span className="bnc-howto__num">4</span>
              <div>
                <strong>Choose who can see it, then submit</strong>
                <span>🔒 <b>Private</b> — goes straight into Nezo for you and your account.</span>
                <span>🌐 <b>Public</b> — the Nezo team reviews it, then everyone in your region can use it.</span>
              </div>
            </li>
          </ol>
        </div>
      </div>

      <div className="bnc-top__form">
      {error && <div className="alert alert--error">⚠️ {error}</div>}
      {success && <div className="alert alert--success">✅ {success}</div>}
      {copyRequest && !copySeededRef.current && copyRequest.regionId && copyRequest.regionId !== selectedRegion && (
        <div className="alert alert--error">⚠️ "{copyRequest.name}" is in another region — switch to {copyRequest.regionId} to copy it.</div>
      )}

      {/* NEW: Side-by-side search and create sections */}
      {/* ── Tab toggle ──────────────────────────────────────────────────── */}
      <div style={{
        display:      'flex',
        border:       '0.5px solid #d1d5db',
        borderRadius: 8,
        overflow:     'hidden',
        marginBottom: 16,
        width:        '100%',
      }}>
        {[
          { key: 'existing', label: 'Load Template Material' },
          { key: 'create', label: 'Create New Material'    },
        ].map(({ key, icon, label }, i) => (
          <button
            key={key}
            onClick={() => setMaterialTab(key)}
            style={{
              flex:            1,
              padding:         '10px 0',
              fontSize:        14,
              fontWeight:      materialTab === key ? 600 : 400,
              cursor:          'pointer',
              border:          'none',
              borderLeft:      i > 0 ? '0.5px solid #d1d5db' : 'none',
              background:      materialTab === key ? '#e3f2fd' : '#fff',
              color:           materialTab === key ? '#1565c0' : '#6b7280',
              transition:      'all .15s',
            }}
          >
            {icon} {label}
          </button>
        ))}
      </div>
 
      {/* ── PANEL A: Load Existing ──────────────────────────────────────── */}
      <div className="bnc-panels">
        <div
          className={`form-container form-container--material bnc-panel ${materialTab === 'existing' ? '' : 'bnc-panel--hidden'}`}
          aria-hidden={materialTab !== 'existing'}
        >
 
          <label className="label">Find a template material</label>
          <SmartMaterialSearch
            name="templateSearch"
            materials={templateOptions}
            value={selectedTemplate?.Name || ''}
            category={selectedTemplate?.Category || ''}
            onChange={(name, cat, raw) => onSelectMaterial(raw?.Id || '')}
            placeholder="Search by name, category or ID…"
            showMetrics={false}
          />
          <small className="hint">
            {materials.length
              ? `${materials.length} materials available for ${regionName} · ⤢ browses them by category`
              : 'No materials found'}
          </small>
 
          {selectedMaterial && (
            <div style={{
              display:      'flex',
              alignItems:   'center',
              gap:          8,
              padding:      '8px 12px',
              background:   '#ecfdf5',
              border:       '1px solid #6ee7b7',
              borderRadius: 6,
              marginTop:    12,
              fontSize:     13,
              color:        '#065f46',
            }}>
              ✅ <strong>{filteredMaterials.find(m => m.Id === selectedMaterial)?.Name || selectedMaterial}</strong> loaded
            </div>
          )}
 
          <div style={{ marginTop: 16 }}>
            <button
              className="btn-next submit-request-button"
              onClick={handleNextClick}
              disabled={!selectedMaterial}
              style={{ opacity: selectedMaterial ? 1 : 0.4 }}
            >
              Continue with this material →
            </button>
          </div>
        </div>

      {/* ── PANEL B: Create New ─────────────────────────────────────────── */}
        <div
          className={`form-container form-container--material bnc-panel ${materialTab === 'create' ? '' : 'bnc-panel--hidden'}`}
          aria-hidden={materialTab !== 'create'}
        >
 
         {/* Row 1: Name + Unit side by side */}
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12, marginBottom: 12, alignItems: 'start' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <label className="label">Material Name *</label>
              <input
                className="input"
                style={{ height: 42 }}
                type="text"
                placeholder="e.g. Timber framing 90×45"
                value={customMaterialName}
                onChange={onCustomNameChange}
                aria-invalid={copyNeedsRename || undefined}
              />
              {copyNeedsRename && (
                <span style={{ color: '#b45309', fontSize: 12.5, fontWeight: 600 }}>
                  ✏️ Give the copy a new name — it can't keep "{copySourceName}".
                </span>
              )}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 19 }}>
              <label className="label">Unit</label>
              <div style={{ height: 5 }}>
                <StyledDropdown
                  name="customMaterialUnit"
                  options={[
                    { value: 'SquareMeters',  label: 'm²' },
                    { value: 'CubicMeters',   label: 'm³' },
                    { value: 'LinearMeters',  label: 'Linear m' },
                    { value: 'Count',         label: 'No.' },
                    ...(customMaterialUnit && !['SquareMeters', 'CubicMeters', 'LinearMeters', 'Count'].includes(customMaterialUnit)
                      ? [{ value: customMaterialUnit, label: customMaterialUnit }]
                      : []),
                  ]}
                  value={customMaterialUnit}
                  onChange={(val) => setCustomMaterialUnit(val)}
                  placeholder="Select unit…"
                />
              </div>
            </div>
          </div>
 
          {/* Row 2: Description */}
          <div style={{ marginBottom: 12 }}>
            <label className="label">Description <span style={{ color: '#9ca3af', fontWeight: 400 }}></span></label>
            <textarea
              className="input"
              placeholder="Brief description of this material"
              value={customMaterialDescription}
              onChange={onCustomDescriptionChange}
              rows={2}
            />
          </div>
 
          {/* Row 3: Category picker — opens modal */}
          <div style={{ marginBottom: 16 }}>
            <label className="label">Category ID *</label>
            <CategoryPickerModal
              categories={visibleCompositeCategories}
              value={customMaterialCategoryId}
              onChange={setCustomMaterialCategoryId}
              placeholder="Required — search and select a category…"
              title="Select Category"
            />
            <small className="hint">
              {visibleCompositeCategories.length} categories available
              {customMaterialCategoryId && <> · Selected: <strong>{(NewMaterialCategories.find(c => c.id === customMaterialCategoryId)?.name) || customMaterialCategoryId}</strong></>}
            </small>
          </div>

          {/* Row 4: full build-up vs cost only (priced in Nezo from the Rate Library) */}
          <div style={{ marginBottom: 8 }}>
            <BuildTypeToggle compact costOnly={costOnlyChecked} onChange={setCostOnlyChecked} />
          </div>
 
          {/* <div style={{ display: 'flex', gap: 8 }}>
            <button
              className="btn-clear"
              style={{ padding: '10px 16px', fontSize: 13, border: '1px solid #d1d5db', borderRadius: 6, background: 'transparent', cursor: 'pointer', color: '#6b7280' }}
              onClick={() => {
                setCustomMaterialName('');
                setCustomMaterialDescription('');
                setCustomMaterialUnit('');
                setCustomMaterialCategoryId('');
                setCatSearch('');
              }}
            >
              ↺ Reset
            </button>
            <button
              className="submit-request-button"
              onClick={handleNextClick}
              disabled={!customMaterialName.trim()}
              style={{
                maxWidth: 200,           // ← replaces flex: 1
                opacity: customMaterialName.trim() ? 1 : 0.4,
              }}
            >
              Add line items →
            </button>
          </div> */}
        </div>
      </div>
      </div>
      </div>

      {/* ENV rows — not needed for a cost-only material */}
      {costOnly ? (
        <div className="form-section">
          <h2 id="environmental-line-items">Environmental Line Items (Carbon / Biogenic / Weight)</h2>
          <p style={{ textAlign: 'center', color: '#4b5563', margin: '8px 0 4px' }}>
            <strong>Cost only</strong> — no build-up needed. After uploading, link this material to a Rate Library
            cost from its chip on Nezo's Materials page. You can come back and add carbon, biogenic and weight later.
          </p>
        </div>
      ) : (
      <>
      {/* ENV rows */}
      <div className="form-section">
        <h2 id="environmental-line-items">Environmental Line Items (Carbon / Biogenic / Weight)</h2>

        <div className="line-items-header" style={{ display:'grid', gridTemplateColumns: GRID_ENV, gap: 12, alignItems:'center' }}>
          <span>⋮⋮</span><span>Material</span><span>Quantity</span><span>Unit</span>
          <span>Carbon Total</span><span>Biogenic Total</span><span>Weight Total</span><span>Actions</span>
        </div>

        {rows.map((r, idx) => (
          <React.Fragment key={r.id}>
            {/* ── HEADING ROW ───────────────────────────────────────────── */}
            {r.isHeading && (
              <div
                className="heading-item"
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, idx)}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '36px 1fr auto',
                  gap: 12, alignItems: 'center', padding: '10px 0',
                  background: r.fromAssembly ? '#ecfdf5' : '#f0f4f8',
                  borderLeft: r.fromAssembly ? '4px solid #2a7f73' : '4px solid #4a90e2',
                }}
              >
                <span className="drag-handle" draggable onDragStart={(e) => handleDragStart(e, idx)} onDragEnd={handleDragEnd} style={{ cursor: 'grab', textAlign: 'center', userSelect: 'none' }}>⋮⋮</span>
                <input
                  className="input input--heading"
                  type="text"
                  value={r.heading || ''}
                  placeholder="Enter heading..."
                  onChange={(e) => handleHeadingChange(r.id, e.target.value)}
                  style={{ fontWeight: 'bold', marginLeft: '8px' }}
                />
                {r.fromAssembly ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10, paddingRight: 8 }}>
                    <span
                      title={`Copied in from the approved assembly "${r.fromAssembly.name}"`}
                      style={{ fontSize: 11, fontWeight: 700, color: '#134e4a', background: '#ccfbf1', border: '1px solid #5eead4', borderRadius: 999, padding: '2px 8px', whiteSpace: 'nowrap' }}
                    >
                      ASSEMBLY
                    </span>
                    <AssemblyScale value={r.fromAssembly.scale} onCommit={(v) => rescaleAssembly(r.fromAssembly.blockId, v)} />
                    <button
                      className="btn btn--danger btn--small"
                      onClick={() => removeAssembly(r.fromAssembly.blockId)}
                      title="Remove this assembly and every line item under it"
                    >
                      Remove
                    </button>
                  </span>
                ) : (
                  <button className="btn btn--danger btn--small" onClick={() => deleteEnvRow(r.id)}>
                    Delete
                  </button>
                )}
              </div>
            )}
 
            {/* ── SUB-HEADING ROW 🆕 ────────────────────────────────────── */}
            {r.isSubHeading && (
              <div
                className="subheading-item"
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, idx)}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '36px 20px 1fr auto',
                  gap: 12, alignItems: 'center',
                  padding: '7px 0 7px 32px',
                  background: '#f7f9fc',
                  borderLeft: '3px solid #a0bfdf',
                }}
              >
                <span className="drag-handle" draggable onDragStart={(e) => handleDragStart(e, idx)} onDragEnd={handleDragEnd} style={{ cursor: 'grab', textAlign: 'center', userSelect: 'none' }}>⋮⋮</span>
                <span style={{ color: '#7a9cbf', fontSize: 16 }}>└</span>
                <input
                  className="input input--subheading"
                  type="text"
                  value={r.subHeading || ''}
                  placeholder="Enter sub-heading..."
                  onChange={(e) => handleSubHeadingChange(r.id, e.target.value)}
                  style={{ fontWeight: 600, fontSize: 13, color: '#2c5282' }}
                />
                <button className="btn btn--danger btn--small" onClick={() => deleteEnvRow(r.id)}>
                  Delete
                </button>
              </div>
            )}
 
            {/* ── DATA ROW ──────────────────────────────────────────────── */}
            {!r.isHeading && !r.isSubHeading && (
              <div
                className="line-item"
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, idx)}
                style={{ display: 'grid', gridTemplateColumns: GRID_ENV, gap: 12, alignItems: 'center', padding: '10px 0' }}
              >
                <span
                  className="drag-handle"
                  draggable
                  onDragStart={(e) => handleDragStart(e, idx)}
                  onDragEnd={handleDragEnd}
                  style={{ cursor: 'grab', textAlign: 'center', userSelect: 'none' }}
                >
                  ⋮⋮
                </span>
                <div
                  className="smart-search-cell"
                  style={{ minWidth: 400 }}
                >
                  <SmartMaterialSearch
                    name={`search-${r.id}`}
                    materials={ENV_ALL}
                    carbonCategories={carbonCategories}
                    biogenicCategories={biogenicCategories}
                    weightCategories={weightCategories}
                    value={r.description}
                    category={r.category}
                    onChange={async (desc, cat, rawItem) => {
                  if (!desc) {
                    updateEnvRow(r.id, {
                      description: '',
                      category: '',
                      unit: '',
                      carbon: {},
                      bio: {},
                      weight: {},
                    });
                    return;
                  }

                  const co2Entry = carbonCategories.find(x => getName(x) === desc);
                  const bioEntry = biogenicCategories.find(x => getName(x) === desc);
                  const wtEntry  = weightCategories.find(x => getName(x) === desc);

                  const buildMetricFromEntry = (entry) => {
                    if (!entry) return null;
                    return {
                      measurementId: entry.MeasurementId || '',
                      code: entry.MeasurementUnitRateId || '',
                      unit: entry.QuantityTypeName || '',
                      value: entry.Value != null ? Number(entry.Value) : null,
                      total: 0,
                    };
                  };

                  let carbon = buildMetricFromEntry(co2Entry);
                  let bio = buildMetricFromEntry(bioEntry);
                  let weight = buildMetricFromEntry(wtEntry);

                  const needsBreakdownFetch =
                    carbon?.value == null ||
                    bio?.value == null ||
                    weight?.value == null;

                  if (needsBreakdownFetch) {
                    const rateId =
                      co2Entry?.MeasurementUnitRateId ||
                      bioEntry?.MeasurementUnitRateId ||
                      wtEntry?.MeasurementUnitRateId ||
                      rawItem?.MeasurementUnitRateId ||
                      '';

                    const matId = rateId.replace(/vquest-(Co2|Co2-Biogenic|Weight).*$/i, '').trim();

                    if (matId) {
                      try {
                        const bd = await fetchBreakdownData(matId);
                        const data = Array.isArray(bd.data?.data) ? bd.data.data : (bd.data || []);

                        const co2 = data.find(d => typeMatchesBase(d.MeasurementType, TYPE_BASE.CO2));
                        const bioRow = data.find(d => typeMatchesBase(d.MeasurementType, TYPE_BASE.BIO));
                        const wt = data.find(d => typeMatchesBase(d.MeasurementType, TYPE_BASE.WEIGHT));

                        if (co2) {
                          carbon = {
                            measurementId: co2.MeasurementType || '',
                            code: co2.MeasurementUnitRateId || '',
                            unit: co2.QuantityTypeName || '',
                            value: Number(co2.Value || 0),
                            total: 0,
                          };
                        }

                        if (bioRow) {
                          bio = {
                            measurementId: bioRow.MeasurementType || '',
                            code: bioRow.MeasurementUnitRateId || '',
                            unit: bioRow.QuantityTypeName || '',
                            value: Number(bioRow.Value || 0),
                            total: 0,
                          };
                        }

                        if (wt) {
                          weight = {
                            measurementId: wt.MeasurementType || '',
                            code: wt.MeasurementUnitRateId || '',
                            unit: wt.QuantityTypeName || '',
                            value: Number(wt.Value || 0),
                            total: 0,
                          };
                        }
                      } catch (err) {
                        console.error('Failed to fetch EPD breakdown values', err);
                      }
                    }
                  }

                  updateEnvRow(r.id, {
                    description: desc,
                    category: cat,
                    unit:
                      carbon?.unit ||
                      bio?.unit ||
                      weight?.unit ||
                      rawItem?.QuantityTypeName ||
                      rawItem?.Unit ||
                      '',
                    carbon: carbon || {},
                    bio: bio || {},
                    weight: weight || {},
                  });
                  const costEntry = costCategories.find(x => getName(x) === desc);

                  if (costEntry) {
                    const currentQty = Number(r.quantity || 0);

                    setCostRows(prev => {
                      const existing = prev.find(cr => cr.envRowId === r.id);

                      const nextRate = {
                        measurementId: costEntry.MeasurementId || '',
                        code: costEntry.MeasurementUnitRateId || '',
                        unit: costEntry.QuantityTypeName || '',
                        value: Number(costEntry.Value || 0),
                        total: Number(costEntry.Value || 0) * currentQty,
                      };

                      if (existing) {
                        return prev.map(cr =>
                          cr.envRowId === r.id
                            ? {
                                ...cr,
                                category: cat || '',
                                description: desc,
                                quantity: currentQty,
                                unit: costEntry.QuantityTypeName || '',
                                rate: nextRate,
                                heading: r.heading || null,
                              }
                            : cr
                        );
                      }

                      return [
                        ...prev,
                        {
                          id: uuidv4(),
                          envRowId: r.id,
                          category: cat || '',
                          description: desc,
                          quantity: currentQty,
                          unit: costEntry.QuantityTypeName || '',
                          rate: nextRate,
                          locked: false,
                          heading: r.heading || null,
                        }
                      ];
                    });
                  }
                }}
                placeholder="Search materials by name, category, or description..."
              />
            </div>

            <input
              type="text"
              value={r.quantity}
              readOnly
              className="input--quantity"
              title="Click to open quantity calculator"
              onFocus={() => { setActiveRowIdx(idx); setModalOpen(true); }}
              style={{ cursor: 'pointer' }}
            />

            <input type="text" readOnly value={r.unit} />
            <input type="number" readOnly value={r.carbon?.total ?? 0} />
            <input type="number" readOnly value={r.bio?.total ?? 0} />
            <input type="number" readOnly value={r.weight?.total ?? 0} />
            <div className="btn-group" style={{ display:'flex', justifyContent:'flex-end' }}>
              <button className="btn btn--danger" onClick={() => deleteEnvRow(r.id)}>Delete</button>
            </div>
          </div>
            )}
          </React.Fragment>
        ))}

        {/* ── Action buttons ──────────────────────────────────────────────── */}
        <div className="section__actions">
          <button className="btn btn--success" onClick={addEnvRow}>
            + Add Line Item
          </button>
          <button className="btn btn--indigo btn--add-heading" onClick={addEnvHeading}>
            + Add Heading
          </button>
          {/* 🆕 */}
          <button className="btn btn--teal btn--add-subheading" onClick={addEnvSubHeading}>
            + Add Sub-Heading
          </button>
          <button
            className="btn btn--success"
            onClick={() => setAddAssemblyOpen(true)}
            title="Copy an approved assembly's line items in, scaled — add several to combine them"
          >
            + Add Assembly
          </button>
        </div>
        <AddAssemblyModal
          isOpen={addAssemblyOpen}
          onClose={() => setAddAssemblyOpen(false)}
          templateOptions={templateOptions}
          compositeUnit={customMaterialUnit}
          loadBuildUp={loadAssemblyBuildUp}
          onAdd={addAssemblyRows}
          categoryFor={findCategoryForDesc}
          isMine={isMyMaterial}
        />

        {/* ENV totals – inline chips */}
        <div className="totals-inline">
          <div className="totals-chip">🌡️ Carbon: <b>{totals.carbon.toFixed(3)}</b> {totals.uCarbon || ''}</div>
          <div className="totals-chip">🌱 Biogenic: <b>{totals.biogenic.toFixed(3)}</b> {totals.uBio || ''}</div>
          <div className="totals-chip">⚖️ Weight: <b>{totals.weight.toFixed(3)}</b> {totals.uWeight || ''}</div>
        </div>
      </div>
      </>
      )}

      {/* COST rows - HIDDEN: Still processes data but invisible to user */}
      <div className="form-section" style={{ display: 'none' }}>
        <h2>Cost Line Items</h2>

        <div className="line-items-header" style={{ display:'grid', gridTemplateColumns: GRID_COST, gap: 12, alignItems:'center' }}>
          <span>Material</span><span>Quantity</span><span>Unit</span><span>Cost Total</span><span>Actions</span>
        </div>

        {costRows.map((r) => (
          <div className="line-item"
               key={r.id}
               style={{ display:'grid', gridTemplateColumns: GRID_COST, gap:12, alignItems:'center', padding:'10px 0' }}>
            {/* SMART SEARCH (replaces Category + Description) */}
            <div
              className="smart-search-cell"
              style={{ minWidth: 400 }}
            >
              <SmartMaterialSearch
                name={`costsearch-${r.id}`}
                materials={costCategories}
                value={r.description}
                category={r.category}
                onChange={(desc, cat) => {
                updateCostRow(r.id, {
                  description: desc || '',
                  category: cat || '',
                });
              }}
                placeholder="Search cost items..."
              />
            </div>

            <input
              type="text"
              value={r.quantity}
              readOnly={r.locked}
              className={r.locked ? 'input--locked' : ''}
              title={r.locked ? 'Locked (from base material)' : 'Click to edit'}
              onChange={r.locked ? undefined : (e) => onCostQtyChange(r.id, e.target.value)}
            />

            <input type="text" readOnly value={r.unit} />
            <input type="number" readOnly value={r.rate?.total ?? 0} />
            <div className="btn-group" style={{ display:'flex', justifyContent:'flex-end' }}>
              <button className="btn btn--danger" onClick={() => deleteCostRow(r.id)}>Delete</button>
            </div>
          </div>
        ))}

        <div className="section__actions">
          <button className="btn btn--success" onClick={addCostRow}>+ Add Cost Line Item</button>
        </div>

        {/* COST totals – inline chip */}
        <div className="totals-inline">
          <div className="totals-chip">💰 Cost: <b>{totals.cost.toFixed(2)}</b> {totals.uCost || ''}</div>
        </div>
      </div>

      {/* comments + who can see it, side by side (stack on narrow screens) */}
      <div className="bnc-finish">
      <div className="form-section bnc-finish__comments">
        <h2>Additional Comments</h2>
        <textarea
          className="input"
          rows={4}
          placeholder="Update the name of the material or tell the reviewer what needs changing or any assumptions they should know about (optional)…"
          value={userComments}
          onChange={(e) => setUserComments(e.target.value)}
        />
      </div>

      <div className="form-section bnc-finish__visibility">
        <h2>Who Can See This Material?</h2>
        <VisibilityToggle value={visibility} onChange={setVisibility} />
      </div>
      </div>

      {/* submit */}
      <div className="form-section">
        <div className="submit-container-build-material" style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
          <button
            type="button"
            className="submit-request-button"
            onClick={handleSubmit}
            disabled={
              !canUpload ||
              submitting ||
              (!costOnly && rows.filter(r => !r.isHeading).length === 0 && costRows.length === 0)
            }
            title={!canUpload
              ? "Your company's reviewers or admins upload to Nezo. You can Save as Draft or Send for Internal Review."
              : (!costOnly && rows.filter(r => !r.isHeading).length === 0 && costRows.length === 0)
                ? 'Add at least one line item (or tick "Cost only")'
                : visibility === 'private' ? 'Upload to Nezo for you and your team' : 'Submit to the Nezo team for review'}
          >
            {submitting ? 'Submitting…' : 'Upload to Nezo'}
          </button>

          {/* 🆕 Send for Review */}
          <button
            type="button"
            className="review-button"
            onClick={handleSendForReview}
            disabled={submitting || (!customMaterialName.trim() && !selectedMaterial)}
            title={(!customMaterialName.trim() && !selectedMaterial)
              ? 'Enter a material name or select an existing material first'
              : 'Save for internal review — no admin notification sent'}
          >
            {submitting ? 'Saving…' : '🔍 Send for Internal Review'}
          </button>
 
          <button
            type="button"
            className="draft-button"
            onClick={handleSaveDraft}
            disabled={submitting || (!customMaterialName.trim() && !selectedMaterial)}
            title={(!customMaterialName.trim() && !selectedMaterial)
              ? 'Enter a material name or select an existing material first'
              : 'Save as draft — no admin notification sent'}
          >
            {submitting ? 'Saving…' : '💾 Save as Draft'}
          </button>
        </div>
 
        <p className="submit-help-text">
          {visibility === 'private'
            ? "Private materials go straight to Nezo for you and everyone on your Nezo account, usually within a minute. Save as Draft keeps it here until you're ready."
            : "Public materials are reviewed by the Nezo team before they're published. Save as Draft keeps it here until you're ready."}
        </p>
      </div>


      <QuantityCalculatorModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSave={(q, rows_) => {
          const row = rows[activeRowIdx];
          if (!row || row.isHeading) return setModalOpen(false);

          // ✅ Live links: apply this row's new quantity, then propagate to linked rows.
          const baseRows = rows.map((r) => (r.id === row.id ? recalcEnvRow({ ...r, quantity: q }) : r));
          const resolvedHistory = { ...rowHistory, [row.id]: rows_ };
          resolveLineItemLinks(baseRows, {
            rowsOf: (item) => resolvedHistory[item.id],
            setRows: (item, rws) => { resolvedHistory[item.id] = rws; },
            setQuantity: (item, qty) => {
              const idx = baseRows.findIndex((b) => b.id === item.id);
              if (idx >= 0) baseRows[idx] = recalcEnvRow({ ...baseRows[idx], quantity: qty });
            },
          });
          setRows(baseRows);
          setRowHistory(resolvedHistory);
          setModalOpen(false);
        }}
        initialRows={rowHistory[rows[activeRowIdx]?.id] || []}
        lineItems={rows.filter(r => !r.isHeading && r.description).map(r => ({
          ...r,
          FactorId: r.id,
          CostMeasurementName: r.description,
          Category: r.category,
          QuantityTypeName: r.unit,
          Factor: r.quantity,
          sectionType: 'Environmental',
        }))}
        sectionType="Environmental"
        // ✅ Cycle-prevention identity must use the same stable linkKey as the links
        // themselves, so currentItemId + the rows-by-id map are keyed by linkKey (not the
        // ephemeral row.id). rowHistory itself stays keyed by row.id for save/load.
        currentItemId={rows[activeRowIdx]?.linkKey}
        lineItemRowsById={rows.reduce((acc, r) => {
          if (!r.isHeading && r.linkKey != null) acc[String(r.linkKey)] = rowHistory[r.id] || [];
          return acc;
        }, {})}
        // ✅ NEW — feed the unit context so the mismatch advisor can activate
        compositeUnit={customMaterialUnit}                   // the composite's output unit (e.g. 'm2')
        materialUnit={rows[activeRowIdx]?.unit || rows[activeRowIdx]?.weight?.unit || rows[activeRowIdx]?.carbon?.unit || ''} // the selected line item's unit (e.g. 'm3')
        materialName={rows[activeRowIdx]?.description ?? ''} // used for thickness preset lookup
        materialWeight={rows[activeRowIdx]?.weight}          // its density, for the Conversion helper
      />
      {/* ✅ Nice success popup */}
      {showThanks && (
        <div
          onClick={() => setShowThanks(false)}
          style={{position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000}}
        >
          <div onClick={(e)=>e.stopPropagation()} style={{background:'#fff', borderRadius:12, padding:24, width:'min(520px,95vw)', textAlign:'center', boxShadow:'0 10px 30px rgba(0,0,0,.15)'}}>
            <div style={{fontSize:48, lineHeight:1, marginBottom:8}}>🎉</div>
            <h2 style={{margin:'4px 0 8px'}}>
              {thanksInfo.autoApproved ? 'Your material is ready in Nezo' : thanksInfo.visibility === 'private' ? 'Uploading to Nezo!' : 'Request sent!'}
            </h2>
            <p>
              {thanksInfo.autoApproved && thanksInfo.costOnly
                ? 'Your material is in Nezo. Link its cost from your Rate Library on the Materials page — and add carbon, biogenic and weight here any time.'
                : thanksInfo.autoApproved
                ? 'Your private material has been added. Go back to Nezo and it will be waiting for you and your team — usually within a minute.'
                : thanksInfo.visibility === 'private'
                  ? "Your private material couldn't be added automatically, so a Nezo admin will check it. It will appear in Nezo for you and your team once approved."
                  : 'Your material has been sent to the Nezo team for review.'}
            </p>
            <div style={{background:'#f6f9ff', border:'1px solid #dbe7ff', borderRadius:8, padding:12, margin:'12px 0', fontSize:14}}>
              <div><b>Request ID:</b> {thanksId}</div>
              <div><b>Region:</b> {regionName}</div>
            </div>
            <div style={{display:'flex', gap:12, justifyContent:'center', flexWrap:'wrap'}}>
              <button className="submit-request-button" onClick={backToNezo}>Back to Nezo</button>
              <button className="submit-request-button" style={{background:'#ffffff', color:'#3d7a72', border:'1.5px solid #3d7a72'}} onClick={() => { setShowForReviewSaved(false); window.location.reload();}}>Build another</button>
            </div>
          </div>
        </div>
      )}

      {showDraftSaved && (
        <div onClick={() => setShowDraftSaved(false)}
          style={{position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000}}>
          <div onClick={(e) => e.stopPropagation()}
            style={{background:'#fff', borderRadius:12, padding:24, width:'min(480px,95vw)', textAlign:'center', boxShadow:'0 10px 30px rgba(0,0,0,.15)'}}>
            <div style={{fontSize:48, lineHeight:1, marginBottom:8}}>💾</div>
            <h2 style={{margin:'4px 0 8px'}}>Draft Saved!</h2>
            <p>Your draft has been saved. Find it under <b>Drafts</b> in My Materials when you're ready to continue.</p>
            <button className="submit-request-button" onClick={() => { setShowDraftSaved(false); window.location.reload(); }}>Got it!</button>
            </div>
        </div>
      )}

      {showForReviewSaved && (
        <div
          onClick={() => setShowForReviewSaved(false)}
          style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background:'#fff', borderRadius:12, padding:24, width:'min(480px,95vw)', textAlign:'center', boxShadow:'0 10px 30px rgba(0,0,0,.15)' }}
          >
            <div style={{ fontSize:48, lineHeight:1, marginBottom:8 }}>🔍</div>
            <h2 style={{ margin:'4px 0 8px' }}>Sent for Review!</h2>
            <p>Your material has been saved for internal review. Find it under <b>For Review</b> in My Materials.</p>
            <button className="submit-request-button" onClick={() => { setShowForReviewSaved(false); window.location.reload(); }}>Got it!</button>
          </div>
        </div>
      )}

      {/* NEW: Next button popup */}
      {showNextPopup && (
        <div
          onClick={() => setShowNextPopup(false)}
          style={{position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000}}
        >
          <div onClick={(e)=>e.stopPropagation()} style={{background:'#fff', borderRadius:12, padding:24, width:'min(480px,95vw)', textAlign:'center', boxShadow:'0 10px 30px rgba(0,0,0,.15)'}}>
            <div style={{fontSize:48, lineHeight:1, marginBottom:8}}>📝</div>
            <h2 style={{margin:'4px 0 8px'}}>Next Step</h2>
            <p>Now select the required products by adding line items for that material</p>
              <div style={{background:'#f0f8ff', border:'1px solid #b3d9ff', borderRadius:8, padding:12, margin:'12px 0', fontSize:14, textAlign:'left'}}>
              <div><b>Environmental Line Items:</b> Add carbon, biogenic, and weight components for your material</div>
              <div style={{marginTop:6}}><b>Tip:</b> Cost information is automatically calculated from your selections</div>
            </div>
            <button className="submit-request-button" onClick={handleGotItClick}>Got it!</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default BuildNewCompositePage;